import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  TimePicker,
  Form,
  InputNumber,
  Typography,
  Select,
  Button,
  Radio,
  Input,
  Space,
  notification,
  FormListFieldData,
  Tooltip,
  Checkbox,
} from "antd";
import {
  MinusCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import useFetchData from "shared/hooks/useFetchData";
import apiClient from "shared/api/client";
import "./Register.css";

interface BaseItem {
  size: string;
  count: number;
  _id?: string;
}
interface CanvasItem {
  size: string;
  code: string;
  price: number;
  realPrice: number;
  _id?: string;
}
interface CanvasCustomer {
  name?: string;
  phone?: string;
  email?: string;
}
interface PaymentBreakdown {
  cash: number;
  card: number;
  account: number;
}

interface FormData {
  _id?: string;
  year: string;
  day: string;
  bookedTime: string;
  packageName: string | string[];
  giftPhoto: boolean;
  pictures: BaseItem[];
  paper: BaseItem[];
  frame: BaseItem[];
  frameAndPaper: BaseItem[];
  canvas: CanvasItem[];
  canvasCustomer?: CanvasCustomer;
  paymentType?: string;
  paymentBreakdown: PaymentBreakdown;
  prePay: number;
  postPay: number;
  addPayment: number;
  minusPayment: number;
  changePackage?: boolean; // ✅ Багц солисон
  description: string;
  createdBy: string;
  createdAt: string;
  giftCardId?: string;
  giftCardCode?: string;
  giftCardPackage?: string;
  giftCardContact?: string;
}

interface CanvasSizeOption {
  value: string;
  label: string;
  price: number;
  realPrice: number;
}
interface SizeOption {
  value: string;
  label: string;
  price: number;
}

export interface PackageItem {
  value: string;
  label: string;
  price: number;
}

export interface PackageGroup {
  label: string;
  options: PackageItem[];
}
const { Option, OptGroup } = Select;

interface GiftCard {
  _id: string;
  package?: string;
  code?: string;
  instagram?: string;
  phone?: string | number;
  comephone?: string | number;
}

const GIFT_CARD_PACKAGE_VALUE = "__gift_card__";

// ================== Constants ==================
const SIZE_OPTIONS: SizeOption[] = [
  { value: "A6", label: "A6", price: 2500 },
  { value: "A5", label: "A5", price: 5000 },
  { value: "A4", label: "A4", price: 10000 },
  { value: "A3", label: "A3", price: 15000 },
];

const FRAME_OPTIONS: SizeOption[] = [
  { value: "A5", label: "A5", price: 10000 },
  { value: "A4", label: "A4", price: 15000 },
  { value: "A3", label: "A3", price: 30000 },
];

const FRAME_AND_PAPER_OPTIONS: SizeOption[] = [
  { value: "A5", label: "A5", price: 13000 },
  { value: "A4", label: "A4", price: 20000 },
  { value: "A3", label: "A3", price: 40000 },
];

const SIZE_OPTIONS_CANVAS: CanvasSizeOption[] = [
  { value: "15x20", label: "15x20", price: 25000, realPrice: 10000 },
  { value: "20x30", label: "20x30", price: 35000, realPrice: 15000 },
  { value: "30x40", label: "30x40", price: 55000, realPrice: 25000 },
  { value: "40x60", label: "40x60", price: 85000, realPrice: 40000 },
  { value: "50x70", label: "50x70", price: 125000, realPrice: 55000 },
  { value: "60x70", label: "60x70", price: 145000, realPrice: 55000 },
  { value: "60x80", label: "60x80", price: 165000, realPrice: 55000 },
  { value: "60x90", label: "60x90", price: 185000, realPrice: 55000 },
  { value: "60x100", label: "60x100", price: 200000, realPrice: 55000 },
];

const getPaymentBreakdownTotal = (paymentBreakdown?: Partial<PaymentBreakdown>) =>
  Number(paymentBreakdown?.cash || 0) +
  Number(paymentBreakdown?.card || 0) +
  Number(paymentBreakdown?.account || 0);

const getPrimaryPaymentType = (paymentBreakdown?: Partial<PaymentBreakdown>) => {
  const entries = [
    ["Cash", Number(paymentBreakdown?.cash || 0)],
    ["Card", Number(paymentBreakdown?.card || 0)],
    ["Account", Number(paymentBreakdown?.account || 0)],
  ].filter(([, amount]) => Number(amount) > 0);

  if (entries.length > 1) return "Mixed";
  return String(entries[0]?.[0] || "");
};

const normalizeGiftCards = (raw: any): GiftCard[] => {
  const items = Array.isArray(raw) ? raw : raw?.data || [];

  return items.filter((item: GiftCard) => item?._id);
};

const getGiftCardContact = (giftCard?: GiftCard) =>
  [giftCard?.comephone || giftCard?.phone, giftCard?.instagram]
    .filter(Boolean)
    .join(" | ");

// ================== Reusable Components ==================
interface DynamicFormListProps {
  name: string;
  label: string;
  isCanvas: boolean;
  sizeOptions: SizeOption[];
  canvasSizeOptions?: CanvasSizeOption[];
  setIsCanvasActive: (active: boolean) => void;
  calculatePrice: (size: string, count: number) => number;
}

const DynamicFormList: React.FC<DynamicFormListProps> = ({
  name,
  label,
  isCanvas,
  sizeOptions,
  canvasSizeOptions,
  setIsCanvasActive,
  calculatePrice,
}) => (
  <Form.List name={name}>
    {(fields, { add, remove }) => (
      <>
        {fields.map(({ key, name, ...restField }) => (
          <FormListItem
            key={key}
            name={name}
            isCanvas={isCanvas}
            restField={{ ...restField, key }}
            sizeOptions={sizeOptions}
            canvasSizeOptions={canvasSizeOptions ?? []}
            calculatePrice={calculatePrice}
            setIsCanvasActive={setIsCanvasActive}
            onRemove={() => remove(name)}
          />
        ))}
        <Button
          type="dashed"
          onClick={() => {
            add({ size: "", count: 0 });
            setIsCanvasActive(true);
          }}
          block
          icon={<PlusOutlined />}
        >
          {label}
        </Button>
      </>
    )}
  </Form.List>
);

interface FormListItemProps {
  name: number;
  isCanvas: boolean;
  restField: Omit<FormListFieldData, "name">;
  sizeOptions: SizeOption[];
  canvasSizeOptions: CanvasSizeOption[];
  calculatePrice: (size: string, count: number) => number;
  setIsCanvasActive: (active: boolean) => void;
  onRemove: () => void;
}

const FormListItem: React.FC<FormListItemProps> = ({
  name,
  isCanvas,
  restField,
  sizeOptions,
  canvasSizeOptions,
  calculatePrice,
  setIsCanvasActive,
  onRemove,
}) => {
  const form = Form.useFormInstance();

  const updatePrice = useCallback(
    (size: string, count: number) => {
      const currentItems = form.getFieldValue(name) || [];
      currentItems[name] = {
        size,
        count,
        price: calculatePrice(size, count),
      };
      form.setFieldsValue({ [name]: currentItems });
    },
    [form, name, calculatePrice]
  );

  return (
    <Space align="baseline" style={{ display: "flex", marginBottom: 8 }}>
      <Form.Item
        {...restField}
        name={[name, "size"]}
        label="Хэмжээ"
        rules={[{ required: true, message: "Хэмжээг оруулна уу!" }]}
        style={{ width: 200 }}
      >
        {isCanvas ? (
          <Select
            options={canvasSizeOptions}
            allowClear
            placeholder="Select size"
            onChange={(value) => {
              const selected = canvasSizeOptions.find(
                (opt) => opt.value === value
              );
              form.setFieldValue(["canvas", name, "price"], selected?.price);
              form.setFieldValue(
                ["canvas", name, "realPrice"],
                selected?.realPrice
              );
            }}
          />
        ) : (
          <Select
            placeholder="Хэмжээ сонгоно уу"
            options={sizeOptions}
            onChange={(size) => {
              const count = form.getFieldValue([name, "count"]) || 0;
              updatePrice(size, count);
            }}
          />
        )}
      </Form.Item>

      {isCanvas ? (
        <>
          <Form.Item {...restField} name={[name, "price"]} label="Price">
            <Input disabled placeholder="Auto calculated" />
          </Form.Item>
          {/* <Form.Item
            {...restField}
            name={[name, "realPrice"]}
            label="Real price"
            style={{ width: "200px" }}
          >
            <Input disabled placeholder="Auto calculated" />
          </Form.Item> */}
        </>
      ) : null}

      {isCanvas ? (
        <Tooltip title="Зурагны код!">
          <Form.Item
            {...restField}
            style={{ width: "200px" }}
            name={[name, "code"]}
            label="Code"
            rules={[{ required: true, message: "Код оруулна уу!" }]}
          >
            <InputNumber placeholder="Зурагны код" />
          </Form.Item>
        </Tooltip>
      ) : (
        <Form.Item
          {...restField}
          name={[name, "count"]}
          label={"Тоо"}
          rules={[{ required: true, message: "Тоо оруулна уу!" }]}
        >
          <InputNumber
            min={0}
            onChange={(count) => {
              const size = form.getFieldValue([name, "size"]);
              updatePrice(size, count || 0);
            }}
          />
        </Form.Item>
      )}

      <MinusCircleOutlined
        onClick={() => {
          onRemove();
          setIsCanvasActive(false);
        }}
      />
    </Space>
  );
};
interface RegisterProps {
  theme?: string; // Optional theme prop if needed
}

const Register: React.FC<RegisterProps> = ({ theme }) => {
  const currentTheme = theme || "light"; // Default to light theme if not provided
  const [form] = Form.useForm<FormData>();
  const [giftPhotoEnabled, setGiftPhotoEnabled] = useState(false);
  const [isCanvasActive, setIsCanvasActive] = useState(false);
  const [selectedTime, setSelectedTime] = useState<Dayjs | null>(null);
  const navigate = useNavigate();
  const [notifyApi, contextHolder] = notification.useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [packages, setPackages] = useState<PackageGroup[]>([]);
  const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
  const selectedPackageName = Form.useWatch("packageName", form);
  const selectedGiftCardId = Form.useWatch("giftCardId", form);
  const isGiftCardPackage = selectedPackageName === GIFT_CARD_PACKAGE_VALUE;

  const showNotification = useCallback(
    (
      title: string,
      message: string,
      type: "success" | "error" | "warning" | "info" = "info"
    ) => {
      notifyApi[type]({
        message: title,
        description: message,
      });
    },
    [notifyApi]
  );

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const [packageRes, giftCardRes] = await Promise.all([
          apiClient.get("/package/get"),
          apiClient.get("/giftcard/get"),
        ]);
        setPackages(packageRes.data);
        setGiftCards(normalizeGiftCards(giftCardRes.data));
      } catch (error) {
        console.error("Package fetch error:", error);
        showNotification(
          "Багц / Gift card татахад алдаа гарлаа",
          "Backend асаалттай эсэх болон REACT_APP_API_URL тохиргоог шалгана уу.",
          "error"
        );
      }
    };

    fetchPackages();
  }, [showNotification]);
  const { refetch } = useFetchData<Response>(
    "/book/get"
  );

  const getPackagePrice = useCallback(
    (packageValue?: string) =>
      packages
        .flatMap((group) => group.options || [])
        .find((option) => option.value === packageValue)?.price || 0,
    [packages]
  );

  const getSelectedPackagePrice = useCallback(
    (value?: string | string[]) => {
      const selectedValue = Array.isArray(value) ? value[0] : value;
      if (selectedValue === GIFT_CARD_PACKAGE_VALUE) return 0;

      return getPackagePrice(selectedValue);
    },
    [getPackagePrice]
  );

  const getSelectedGiftCard = useCallback(
    (giftCardId?: string) =>
      giftCards.find((giftCard) => giftCard._id === giftCardId),
    [giftCards]
  );

  const getResolvedPackageName = useCallback(
    (values: Partial<FormData>) => {
      if (values.packageName === GIFT_CARD_PACKAGE_VALUE) {
        return values.giftCardPackage || getSelectedGiftCard(values.giftCardId)?.package || "";
      }

      return Array.isArray(values.packageName)
        ? values.packageName[0] || ""
        : values.packageName || "";
    },
    [getSelectedGiftCard]
  );

  const giftCardOptions = useMemo(
    () =>
      giftCards
        .filter((giftCard) => giftCard.package || giftCard.code)
        .map((giftCard) => ({
          value: giftCard._id,
          label: [
            giftCard.code ? `Код: ${giftCard.code}` : undefined,
            giftCard.package,
            getGiftCardContact(giftCard),
          ]
            .filter(Boolean)
            .join(" | "),
        })),
    [giftCards]
  );

  useEffect(() => {
    if (!isGiftCardPackage) {
      form.setFieldsValue({
        giftCardId: undefined,
        giftCardCode: undefined,
        giftCardPackage: undefined,
        giftCardContact: undefined,
      });
    }
  }, [form, isGiftCardPackage]);

  useEffect(() => {
    if (!selectedGiftCardId) return;

    const selectedGiftCard = getSelectedGiftCard(selectedGiftCardId);
    form.setFieldsValue({
      giftCardCode: selectedGiftCard?.code,
      giftCardPackage: selectedGiftCard?.package,
      giftCardContact: getGiftCardContact(selectedGiftCard),
    });
  }, [form, getSelectedGiftCard, selectedGiftCardId]);

  // === PostPay тооцоолол ===
  const calculatePostPay = useCallback(() => {
    const values = form.getFieldsValue();

    const basePackagePrice = Array.isArray(values.packageName)
      ? values.packageName.reduce((sum, v) => {
          return sum + getSelectedPackagePrice(v);
        }, 0)
      : getSelectedPackagePrice(values.packageName);

    const calc = (items: BaseItem[] = [], opts: SizeOption[]) =>
      (items || []).reduce((acc, item) => {
        const price = opts.find((o) => o.value === item.size)?.price || 0;
        return acc + price * (item.count || 0);
      }, 0);

    const calcCanvas = (items: CanvasItem[] = []) =>
      (items || []).reduce((acc, item) => {
        return acc + (item.price || 0);
      }, 0);

    const totalBeforePrepay =
      basePackagePrice +
      calc(values.paper, SIZE_OPTIONS) +
      calc(values.frame, FRAME_OPTIONS) +
      calc(values.frameAndPaper, FRAME_AND_PAPER_OPTIONS) +
      calcCanvas(values.canvas) +
      (values.addPayment || 0);

    const penalty = values.changePackage
      ? Math.round(basePackagePrice * 0.25)
      : 0;

    const postPay = Math.max(
      0,
      totalBeforePrepay - (values.prePay || 0) - (values.minusPayment || 0)
    );

    return postPay + penalty;
  }, [form, getSelectedPackagePrice]);

  // useEffect(() => {
  //   form.setFieldsValue({
  //     postPay: calculatePostPay(),
  //   });
  // }, [packages]);

  const handleSubmit = useCallback(
    async (values: FormData) => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        const bookedTime = dayjs(values.bookedTime);
        const year = new Date().getFullYear();
        const userId = localStorage.getItem("userId");
        const paymentBreakdown = {
          cash: Number(values.paymentBreakdown?.cash || 0),
          card: Number(values.paymentBreakdown?.card || 0),
          account: Number(values.paymentBreakdown?.account || 0),
        };
        const splitPostPay = getPaymentBreakdownTotal(paymentBreakdown);
        const calculatedPostPay = calculatePostPay();
        const selectedGiftCard = getSelectedGiftCard(values.giftCardId);
        const resolvedPackageName = getResolvedPackageName(values);

        if (splitPostPay !== calculatedPostPay) {
          showNotification(
            "Төлбөрийн дүн таарахгүй байна",
            `Бэлэн/Карт/Данс нийлбэр ${splitPostPay.toLocaleString()}₮ байна. Үлдэгдэл ${calculatedPostPay.toLocaleString()}₮-тэй тэнцэх ёстой.`,
            "warning"
          );
          return;
        }

        const dataToSend = {
          ...values,
          packageName: resolvedPackageName,
          giftCardId: selectedGiftCard?._id,
          giftCardCode: selectedGiftCard?.code,
          giftCardPackage: selectedGiftCard?.package,
          giftCardContact: getGiftCardContact(selectedGiftCard),
          paymentBreakdown,
          prePay: Number(values.prePay || 0),
          postPay: calculatedPostPay,
          paymenType: getPrimaryPaymentType(paymentBreakdown),
          canvasCustomer: {
            name: values.canvasCustomer?.name?.trim() || "",
            phone: values.canvasCustomer?.phone?.trim() || "",
            email: values.canvasCustomer?.email?.trim() || "",
          },
          year: year,
          day: bookedTime.format("MM-DD"),
          bookedTime: bookedTime.format("HH:mm"),
          createdBy: userId,
        };
        const response = await apiClient.post("/book/create", dataToSend);

        if (response.status === 200 || response.status === 201) {
          showNotification("Амжилттай", "Бүртгэгдлээ", "success");
          setTimeout(() => {
            refetch();
            navigate("/bookedList");
          }, 1000);
        }
      } catch (error) {
        const errorMessage = axios.isAxiosError(error)
          ? error.response?.data?.message || "An error occurred"
          : "Бүртгэлтэй байна";
        showNotification("Уучлаарай", errorMessage, "error");
        console.error("Error submitting form:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      calculatePostPay,
      getResolvedPackageName,
      getSelectedGiftCard,
      isSubmitting,
      showNotification,
      navigate,
      refetch,
    ]
  );

  // Form change бүрт үлдэгдлийг дахин тооцоолох
  const handleValuesChange = useCallback(() => {
    const value = calculatePostPay();

    form.setFieldsValue({
      postPay: value,
    });
  }, [form, calculatePostPay]);

  return (
    <div
      className={`register-container ${
        currentTheme === "dark" ? "dark-theme" : "light-theme"
      }`}
    >
      {contextHolder}
      <h1
        className="register-title"
        style={{ color: "var(--text-strong)" }}
      >
        Цагийн бүртгэл
      </h1>

      <Form<FormData>
        form={form}
        layout="horizontal"
        initialValues={{
          paymentBreakdown: { cash: 0, card: 0, account: 0 },
          prePay: 0,
          postPay: 0,
          changePackage: false,
        }}
        onFinish={handleSubmit}
        onValuesChange={handleValuesChange}
        className="register-form"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleSubmit(form.getFieldsValue() as FormData);
          }
        }}
      >
        <Form.Item
          style={{ flex: 1 }}
          className="form-item"
          label="Өдөр"
          name="date"
        >
          <Typography>{selectedTime?.format("YYYY-MM-DD")}</Typography>
        </Form.Item>

        <div className="form-row">
          <Form.Item
            name="bookedTime"
            label="Цаг"
            rules={[{ required: true, message: "Цаг сонгоно уу!" }]}
            className="form-item"
            style={{ flex: 1 }}
          >
            <TimePicker
              format="HH:mm"
              minuteStep={15}
              style={{ width: "100%" }}
              onChange={setSelectedTime}
            />
          </Form.Item>

          <Form.Item
            name="packageName"
            label="Багц"
            rules={[{ required: true, message: "Багц сонгоно уу!" }]}
            className="form-item"
            style={{ flex: 2 }}
          >
            <Select
              allowClear
              showSearch
              placeholder="Багц сонгоно уу"
              optionFilterProp="children"
              onDropdownVisibleChange={() =>
                form.validateFields(["packageName"])
              }
            >
              {packages.map((group) => (
                <OptGroup key={group.label} label={group.label}>
                  {group.options.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </OptGroup>
              ))}
              <Option value={GIFT_CARD_PACKAGE_VALUE}>Gift card</Option>
            </Select>
          </Form.Item>
        </div>

        {isGiftCardPackage && (
          <div className="gift-card-register-panel">
            <Form.Item
              name="giftCardId"
              label="Gift card"
              rules={[{ required: true, message: "Gift card сонгоно уу!" }]}
            >
              <Select
                allowClear
                showSearch
                placeholder="Gift card код / багц / утсаар хайх"
                options={giftCardOptions}
                optionFilterProp="label"
              />
            </Form.Item>

            <div className="gift-card-autofill-grid">
              <Form.Item  name="giftCardPackage" label="Gift card багц">
                <Input disabled variant="filled"  placeholder="Автоматаар бөглөгдөнө" />
              </Form.Item>
              <Form.Item name="giftCardCode" label="Gift card код">
                <Input disabled placeholder="Автоматаар бөглөгдөнө" />
              </Form.Item>
              <Form.Item name="giftCardContact" label="Утас / Instagram">
                <Input disabled placeholder="Автоматаар бөглөгдөнө" />
              </Form.Item>
            </div>
          </div>
        )}

        <div className="form-section">
          <Typography.Title level={4} className="section-title">
            Дагалдах зураг
          </Typography.Title>
          <Form.Item
            name="giftPhoto"
            label="Дагалдах зураг авсан эсэх?"
            className="radio-group"
          >
            <Radio.Group
              style={{ display: "flex" }}
              onChange={(e) => setGiftPhotoEnabled(e.target.value)}
            >
              <Radio value={true}>Тийм</Radio>
              <Radio value={false}>Үгүй</Radio>
            </Radio.Group>
          </Form.Item>

          {giftPhotoEnabled && (
            <DynamicFormList
              name="pictures"
              isCanvas={false}
              label="Дагалдах зураг нэмэх"
              sizeOptions={SIZE_OPTIONS}
              setIsCanvasActive={() => {}}
              calculatePrice={(s, c) =>
                SIZE_OPTIONS.find((o) => o.value === s)?.price! * c
              }
            />
          )}
        </div>

        <div className="form-column">
          <div className="form-section">
            <Typography.Title level={4} className="section-title">
              Нэмэлт зураг
            </Typography.Title>
            <DynamicFormList
              name="paper"
              isCanvas={false}
              label="Нэмэлт зураг нэмэх"
              sizeOptions={SIZE_OPTIONS}
              setIsCanvasActive={() => {}}
              calculatePrice={(s, c) =>
                SIZE_OPTIONS.find((o) => o.value === s)?.price! * c
              }
            />
          </div>

          <div className="form-section">
            <Typography.Title level={4} className="section-title">
              Нэмэлт жааз
            </Typography.Title>
            <DynamicFormList
              name="frame"
              isCanvas={false}
              label="Жааз нэмэх"
              sizeOptions={FRAME_OPTIONS}
              setIsCanvasActive={() => {}}
              calculatePrice={(s, c) =>
                FRAME_OPTIONS.find((o) => o.value === s)?.price! * c
              }
            />
          </div>

          <div className="form-section">
            <Typography.Title level={4} className="section-title">
              Нэмэлт жаазтай зураг
            </Typography.Title>
            <DynamicFormList
              name="frameAndPaper"
              isCanvas={false}
              label="Жаазтай зураг нэмэх"
              sizeOptions={FRAME_AND_PAPER_OPTIONS}
              setIsCanvasActive={() => {}}
              calculatePrice={(s, c) =>
                FRAME_AND_PAPER_OPTIONS.find((o) => o.value === s)?.price! * c
              }
            />
          </div>

          <div className="form-section">
            <Typography.Title level={4} className="section-title">
              Канвас зураг
            </Typography.Title>
            <DynamicFormList
              name="canvas"
              isCanvas={true}
              label="Канвас зураг нэмэх"
              sizeOptions={SIZE_OPTIONS_CANVAS}
              canvasSizeOptions={SIZE_OPTIONS_CANVAS}
              setIsCanvasActive={setIsCanvasActive}
              calculatePrice={(s, c) =>
                SIZE_OPTIONS_CANVAS.find((o) => o.value === s)?.price! * c
              }
            />
          </div>

          {isCanvasActive && (
            <div className="form-section canvas-customer-section">
              <Typography.Title level={4} className="section-title">
                Canvas захиалагч
              </Typography.Title>
              <div className="canvas-customer-grid">
                <Form.Item
                  name={["canvasCustomer", "name"]}
                  label="Нэр"
                  rules={[
                    {
                      required: true,
                      message: "Canvas захиалагчийн нэр оруулна уу",
                    },
                  ]}
                >
                  <Input placeholder="Жишээ: Mina" />
                </Form.Item>
                <Form.Item
                  name={["canvasCustomer", "phone"]}
                  label="Утас"
                  rules={[
                    {
                      required: true,
                      message: "Canvas захиалагчийн утас оруулна уу",
                    },
                  ]}
                >
                  <Input placeholder="+97680097948" />
                </Form.Item>
                <Form.Item
                  name={["canvasCustomer", "email"]}
                  label="И-мэйл"
                >
                  <Input placeholder="email@example.com" />
                </Form.Item>
              </div>
            </div>
          )}
        </div>

        <div
          className="form-row payment-section"
          style={{ color: "var(--text)" }}
        >
          <div
            style={{ color: "var(--text)" }}
          >
            <Form.Item
              name="prePay"
              label="Урьдчилгаа төлбөр"
              labelCol={{
                style: {
                  color: "var(--text)",
                },
              }}
              rules={[
                { required: true, message: "Урьдчилгаа төлбөр оруулна уу!" },
              ]}
            >
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                className="payment-item"
              />
            </Form.Item>

            <div className="payment-breakdown-grid">
              <Form.Item
                name={["paymentBreakdown", "cash"]}
                label="Бэлэн"
                labelCol={{
                  style: {
                    color: "var(--text)",
                  },
                }}
              >
                <InputNumber
                  min={0}
                  className="payment-item"
                  placeholder="Бэлэн"
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item
                name={["paymentBreakdown", "card"]}
                label="Карт"
                labelCol={{
                  style: {
                    color: "var(--text)",
                  },
                }}
              >
                <InputNumber
                  min={0}
                  className="payment-item"
                  placeholder="Карт"
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item
                name={["paymentBreakdown", "account"]}
                label="Данс"
                labelCol={{
                  style: {
                    color: "var(--text)",
                  },
                }}
              >
                <InputNumber
                  min={0}
                  className="payment-item"
                  placeholder="Данс"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </div>
            <Form.Item
              name="postPay"
              label="Үлдэгдэл төлбөр"
              labelCol={{
                style: {
                  color: "var(--text)",
                },
              }}
            >
              <InputNumber
                disabled
                className="payment-item"
                placeholder="Auto calculated"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>

          <div>
            <Tooltip title="Нэмэлт төлбөр!(Жш:) Хувцас багц сольсон г.м">
              <Form.Item
                name="addPayment"
                label="Нэмэлт төлбөр?"
                labelCol={{
                  style: {
                    color: "var(--text)",
                  },
                }}
              >
                <InputNumber
                  className="payment-item"
                  placeholder="Нэмэлт төлбөр"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Tooltip>

            <Tooltip title="Хямдрал үзүүлсэн бол!(Жш:) Жааз зураг г.м">
              <Form.Item
                name="minusPayment"
                label="Хямдрал?"
                labelCol={{
                  style: {
                    color: "var(--text)",
                  },
                }}
              >
                <InputNumber
                  className="payment-item"
                  placeholder="Хямдрал үзүүлсэн бол?"
                  style={{
                    width: "100%",
                    color: "var(--text)",
                  }}
                />
              </Form.Item>
            </Tooltip>

            <Tooltip title="Багц солисон">
              <Form.Item
                name="changePackage"
                label="Багц солисон"
                valuePropName="checked"
              >
                <Checkbox />
              </Form.Item>
            </Tooltip>
          </div>
        </div>

        <Tooltip title="Канвас захиалсан бол утасны дугаар тэмдэглэнэ үү мөн нэмэлт төлбөрын талаар тайлбар оруулах!(Жш:) Хувцас багц сольсон">
          <Form.Item
            labelCol={{
              style: { color: "var(--text)" },
            }}
            name="description"
            rules={[
              {
                required: isCanvasActive ? true : false,
                message: "Канвас захиалсан бол утасны дугаар тэмдэглэнэ үү!",
              },
            ]}
            label="Тайлбар"
          >
            <Input placeholder="Канвас захиалсан бол утасны дугаар тэмдэглэнэ үү мөн нэмэлт төлбөрын талаар тайлбар оруулах!(Жш:) Хувцас багц сольсон" />
          </Form.Item>
        </Tooltip>

        <Form.Item
          labelCol={{
            style: { color: "var(--text)" },
          }}
        >
          <Button
            type="primary"
            htmlType="submit"
            block
            className="submit-button"
            disabled={isSubmitting}
          >
            Батлах
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default Register;
